import React from "react";
import { Dialog } from "primereact/dialog";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  CreateOrganizationPayload,
  Organization,
  OrganizationAddress,
  OrganizationInvoicingInformation,
  UpdateOrganizationPayload,
} from "../api/models/organization.models";
import useOrganizations from "../hooks/useOrganizations";
import { Country } from "../api/models/countries.models";
import { Dropdown } from "primereact/dropdown";
import { getClassLabelFromAkeneoObject } from "../utils/akeneoUtils";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  gap: 0.5rem;

  & .p-inputtext {
    width: 100%;
  }

  & label {
    font-size: 12px;
  }
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const TwoColumnRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 1rem;

  & > div {
    width: 100%;
  }
`;

const DialogHeader = styled.h4`
  font-weight: 500;
  font-size: 20px;
  letter-spacing: -0.4px;
  margin-top: 30px;
`;

interface EditOrganizationDialogProps {
  organization?: Organization;
  show: boolean;
  onHide: () => void;
  companyId: number;
  countries?: Country[];
}

interface Inputs {
  organizationName: string;
  address: OrganizationAddress | null;
  invoicingInformation: OrganizationInvoicingInformation | null;
}

const EditOrganizationDialog: React.FC<EditOrganizationDialogProps> = ({
  organization,
  show,
  onHide,
  companyId,
  countries,
}) => {
  const isNewOrganization = organization === undefined;
  const [locale] = useUIStore(useShallow((state: UIState) => [state.locale]));

  const { t } = useTranslation();

  const {
    updateExistingOrganization,
    isUpdatingOrganization,
    createNewOrganization,
    isCreatingOrganization,
  } = useOrganizations();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      organizationName: isNewOrganization ? "" : organization?.organizationName,
      address:
        isNewOrganization || !organization?.addresses?.length
          ? null
          : organization?.addresses[0] || null,
      invoicingInformation:
        isNewOrganization || !organization?.invoicingInformation
          ? null
          : organization?.invoicingInformation || null,
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: Inputs) => {
    const selectedCountry = countries?.find(
      (country) => country.id === Number(data.address?.country)
    );

    if (isNewOrganization) {
      const newOrganizationPayload: CreateOrganizationPayload = {
        companyId,
        organizationName: data.organizationName,
        addresses:
          data.address?.addressLine ||
          data.address?.city ||
          data.address?.postCode ||
          data.address?.country?.id
            ? [
                {
                  ...data.address,
                  country: {
                    id: selectedCountry?.id as number,
                  },
                },
              ]
            : undefined,
        invoicingInformation:
          data.invoicingInformation?.email ||
          data.invoicingInformation?.invoiceReference ||
          data.invoicingInformation?.ovtNumber ||
          data.invoicingInformation?.operator
            ? data.invoicingInformation
            : undefined,
      };

      const result = await createNewOrganization(newOrganizationPayload);
      onHide();
    } else {
      const updatedOrganizationPayload: UpdateOrganizationPayload = {
        id: organization?.id,
        companyId,
        organizationName: data.organizationName,
        addresses: organization?.addresses
          ? [
              {
                ...data.address,
                country: {
                  id: selectedCountry?.id as number,
                },
                id: organization?.addresses[0]?.id,
              },
            ]
          : [
              {
                ...data.address,
              },
            ],
        invoicingInformation: {
          ...data.invoicingInformation,
          id: organization?.invoicingInformation?.id,
        },
      };

      updateExistingOrganization(updatedOrganizationPayload, () => {
        onHide();
      });
    }
  };

  const getFormErrorMessage = (name: string) => {
    return errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : (
      <></>
    );
  };

  React.useEffect(() => {
    reset({
      organizationName: organization?.organizationName || "",
      address: organization?.addresses?.[0] || null,
      invoicingInformation: organization?.invoicingInformation || null,
    });
  }, [organization, reset]);

  const loading = isUpdatingOrganization || isCreatingOrganization;

  return (
    <Dialog
      header={t("header-organization-contact-information")}
      visible={show}
      style={{ width: "600px" }}
      onHide={onHide}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-field">
          <InputContainer>
            <label htmlFor="name">{t("label-organization-name")}</label>
            <Controller
              name="organizationName"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => (
                <InputText
                  id="organizationName"
                  {...field}
                  autoFocus
                  placeholder={t("label-organization-name")}
                />
              )}
            />
            {getFormErrorMessage("organizationName")}
          </InputContainer>
        </div>
        <div className="p-field">
          <InputContainer>
            <label htmlFor="address.country">{t("label-country")}</label>
            <Controller
              name="address.country"
              control={control}
              render={({ field }) => (
                <Dropdown
                  id="address.country"
                  {...field}
                  options={countries?.map((country) => ({
                    label:
                      getClassLabelFromAkeneoObject(locale, country) ||
                      country.nameFi,
                    value: country.id,
                  }))}
                  placeholder={t("label-select-country")}
                  showClear
                  onChange={(e) => {
                    field.onChange(e.value);
                  }}
                />
              )}
            />
            {getFormErrorMessage("address.country")}
          </InputContainer>
        </div>
        <div className="p-field">
          <InputContainer>
            <label htmlFor="address">{t("label-organization-address")}</label>
            <Controller
              name="address.addressLine"
              control={control}
              render={({ field }) => (
                <InputText
                  id="address.addressLine"
                  {...field}
                  placeholder={t("label-organization-address")}
                />
              )}
            />
            {getFormErrorMessage("address.addressLine")}
          </InputContainer>
        </div>

        <div className="p-field">
          <TwoColumnRow>
            <InputContainer>
              <label htmlFor="address.postCode">{t("label-postcode")}</label>
              <Controller
                name="address.postCode"
                control={control}
                render={({ field }) => (
                  <InputText
                    id="address.postCode"
                    {...field}
                    placeholder={t("label-postcode")}
                  />
                )}
              />
              {getFormErrorMessage("address.postcode")}
            </InputContainer>
            <InputContainer>
              <label htmlFor="address.city">{t("label-city")}</label>
              <Controller
                name="address.city"
                control={control}
                render={({ field }) => (
                  <InputText
                    id="address.city"
                    {...field}
                    placeholder={t("label-city")}
                  />
                )}
              />
              {getFormErrorMessage("address.city")}
            </InputContainer>
          </TwoColumnRow>
        </div>

        <DialogHeader className="billing-header">
          {t("label-billing-information")}
        </DialogHeader>

        <div className="p-field">
          <InputContainer>
            <label htmlFor="invoicingInformation.invoiceReference">
              {t("label-invoice-reference")}
            </label>
            <Controller
              name="invoicingInformation.invoiceReference"
              control={control}
              render={({ field }) => (
                <InputText
                  id="invoicingInformation.invoiceReference"
                  {...field}
                  placeholder={t("label-invoice-reference")}
                />
              )}
            />
            {getFormErrorMessage("invoicingInformation.email")}
          </InputContainer>
        </div>

        <div className="p-field">
          <TwoColumnRow>
            <InputContainer>
              <label htmlFor="invoicingInformation.ovtNumber">
                {t("label-ovt-number")}
              </label>
              <Controller
                name="invoicingInformation.ovtNumber"
                control={control}
                render={({ field }) => (
                  <InputText
                    id="invoicingInformation.ovtNumber"
                    disabled={true}
                    {...field}
                    placeholder={t("label-ovt-number")}
                  />
                )}
              />
              {getFormErrorMessage("invoicingInformation.ovtNumber")}
            </InputContainer>
            <InputContainer>
              <label htmlFor="invoicingInformation.operator">
                {t("label-operator")}
              </label>
              <Controller
                name="invoicingInformation.operator"
                control={control}
                render={({ field }) => (
                  <InputText
                    id="invoicingInformation.operator"
                    {...field}
                    placeholder={t("label-operator")}
                    disabled={true}
                  />
                )}
              />
              {getFormErrorMessage("invoicingInformation.operator")}
            </InputContainer>
          </TwoColumnRow>
        </div>

        <div className="p-field">
          <InputContainer>
            <label htmlFor="invoicingInformation.email">
              {t("label-invoicing-email")}
            </label>
            <Controller
              name="invoicingInformation.email"
              control={control}
              render={({ field }) => (
                <InputText
                  id="invoicingInformation.email"
                  {...field}
                  placeholder={t("label-email")}
                  disabled={true}
                />
              )}
            />
            {getFormErrorMessage("invoicingInformation.email")}
          </InputContainer>
        </div>

        <DialogActions>
          <Button
            id="cancel-edit-organization"
            type="button"
            label={t("cmd-cancel")}
            onClick={onHide}
            outlined
          />
          <Button
            id="save-edit-organization"
            type="submit"
            label={t("cmd-save")}
            loading={loading}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditOrganizationDialog;
